import { useState, useEffect, useMemo } from 'react';
import { businessIntelligenceAPI, sharepointAPI, exchangeAPI } from '../lib/api';
import {
  FiClock, FiPlus, FiTrash2, FiEdit2, FiPlay, FiToggleLeft, FiToggleRight,
  FiAlertCircle, FiCheck, FiLoader, FiX, FiFolder, FiMail,
  FiCloud, FiDatabase, FiChevronRight, FiChevronLeft, FiSearch, FiEye,
  FiRotateCcw,
} from 'react-icons/fi';

const FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily',  label: 'Daily'  },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00`,
}));

function patternToRegexPreview(pattern) {
  let result = '';
  let i = 0;
  while (i < pattern.length) {
    const ch = pattern[i];
    if (ch === '0') {
      let count = 0;
      while (i < pattern.length && pattern[i] === '0') { count++; i++; }
      result += `[0-9]{${count}}`;
    } else if (ch === 'A' || ch === 'Z') {
      let count = 0;
      while (i < pattern.length && (pattern[i] === 'A' || pattern[i] === 'Z')) { count++; i++; }
      result += `[A-Za-z]{${count}}`;
    } else {
      result += ch;
      i++;
    }
  }
  return result;
}

function StatusBadge({ status }) {
  if (!status) return null;
  const map = {
    success: 'bg-green-100 text-green-700',
    error:   'bg-red-100 text-red-700',
    running: 'bg-blue-100 text-blue-700',
    queued:  'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function FrequencyLabel({ s }) {
  const val = s.frequency_value === 1 ? '' : `every ${s.frequency_value} `;
  const map = { hourly: 'hour(s)', daily: 'day(s)', weekly: 'week(s)', monthly: 'month(s)' };
  const at = s.frequency_type !== 'hourly' ? ` at ${String(s.run_at_hour ?? 6).padStart(2,'0')}:00` : '';
  return <span className="text-xs text-gray-500">{val}{map[s.frequency_type]}{at}</span>;
}

export default function BISchedulerSettings() {
  const [schedulers, setSchedulers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingScheduler, setEditingScheduler] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [previewResult, setPreviewResult] = useState(null); // { scheduler, refs, excluded }
  const [backfillScheduler, setBackfillScheduler] = useState(null); // scheduler being configured for backfill
  const [backfillDays, setBackfillDays] = useState('90');
  const [backfillSubmitting, setBackfillSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [schRes, grpRes] = await Promise.all([
        businessIntelligenceAPI.getSchedulers(),
        businessIntelligenceAPI.getGroups(),
      ]);
      setSchedulers(schRes.data.schedulers || []);
      setGroups(grpRes.data.groups || []);
    } catch (e) {
      setError('Failed to load schedulers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingScheduler) {
        await businessIntelligenceAPI.updateScheduler(editingScheduler.id, data);
        setSuccess('Scheduler updated');
      } else {
        await businessIntelligenceAPI.createScheduler(data);
        setSuccess('Scheduler created');
      }
      setShowModal(false);
      setEditingScheduler(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save scheduler');
    }
  };

  const handleToggle = async (s) => {
    setTogglingId(s.id);
    try {
      await businessIntelligenceAPI.updateScheduler(s.id, { is_active: !s.is_active });
      setSchedulers(prev => prev.map(x => x.id === s.id ? { ...x, is_active: !s.is_active } : x));
    } catch { setError('Failed to toggle scheduler'); }
    finally { setTogglingId(null); }
  };

  const handleRun = async (s) => {
    setRunningId(s.id);
    setError(null);
    try {
      const res = await businessIntelligenceAPI.runScheduler(s.id);
      if (res.data.queued) {
        setSuccess(res.data.message || 'Scheduler queued — cron will pick it up within 5 minutes.');
      } else {
        const sum = res.data.summary || {};
        setSuccess(`Run complete — ${sum.sessions_created || 0} created, ${sum.sessions_updated || 0} updated, ${sum.docs_added || 0} docs added`);
      }
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Run failed');
    } finally {
      setRunningId(null);
    }
  };

  const handleBackfillSubmit = async () => {
    const days = parseInt(backfillDays, 10);
    if (!backfillScheduler || !days || days < 1 || days > 1500) {
      setError('Enter a number of days between 1 and 1500');
      return;
    }
    setBackfillSubmitting(true);
    setError(null);
    try {
      const res = await businessIntelligenceAPI.backfillScheduler(backfillScheduler.id, days);
      setSuccess(res.data.message || `Backfill queued for the last ${days} days.`);
      setBackfillScheduler(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Backfill request failed');
    } finally {
      setBackfillSubmitting(false);
    }
  };

  const handlePreview = async (s) => {
    setPreviewingId(s.id);
    setError(null);
    try {
      const res = await businessIntelligenceAPI.previewScheduler(s.id);
      setPreviewResult({ scheduler: s, ...res.data });
    } catch (e) {
      setError(e.response?.data?.error || 'Preview failed');
    } finally {
      setPreviewingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduler?')) return;
    setDeletingId(id);
    try {
      await businessIntelligenceAPI.deleteScheduler(id);
      setSchedulers(prev => prev.filter(s => s.id !== id));
      setSuccess('Scheduler deleted');
    } catch { setError('Failed to delete scheduler'); }
    finally { setDeletingId(null); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

  if (loading) return (
    <div className="flex items-center gap-2 text-gray-400 py-8">
      <FiLoader className="animate-spin w-4 h-4" /> Loading schedulers…
    </div>
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
          <FiAlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><FiX className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-2 text-sm">
          <FiCheck className="w-4 h-4 flex-shrink-0" /> {success}
          <button onClick={() => setSuccess(null)} className="ml-auto"><FiX className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Server-Side Schedulers</h3>
          <p className="text-xs text-gray-500 mt-0.5">Automatically discover and create BI runs based on patterns in folders and emails</p>
        </div>
        <button
          onClick={() => { setEditingScheduler(null); setShowModal(true); }}
          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg"
        >
          <FiPlus className="w-4 h-4" /> New Scheduler
        </button>
      </div>

      {schedulers.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
          <FiClock className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No schedulers yet. Create one to automate BI run creation.
        </div>
      ) : (
        <div className="space-y-3">
          {schedulers.map(s => (
            <div key={s.id} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-800 text-sm">{s.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                      {s.business_intelligence_groups?.name || s.group_id}
                    </span>
                    <StatusBadge status={s.last_run_status} />
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <FrequencyLabel s={s} />
                    <span className="text-xs text-gray-400">•</span>
                    {s.date_range_mode === 'fixed'
                      ? <span className="text-xs text-gray-500">{s.date_range_start} – {s.date_range_end}</span>
                      : <span className="text-xs text-gray-500">{s.date_range_days}d rolling</span>
                    }
                    {s.entry_stage === 'rfq' && <span className="text-xs text-orange-600 font-medium">RFQ</span>}
                    {s.auto_complete && <span className="text-xs text-amber-600 font-medium">Auto-complete</span>}
                    {s.auto_archive && <span className="text-xs text-purple-600 font-medium">Auto-archive</span>}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggle(s)}
                    disabled={togglingId === s.id}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    title={s.is_active ? 'Disable' : 'Enable'}
                  >
                    {togglingId === s.id
                      ? <FiLoader className="w-5 h-5 animate-spin" />
                      : s.is_active
                        ? <FiToggleRight className="w-5 h-5 text-green-500" />
                        : <FiToggleLeft className="w-5 h-5 text-gray-400" />
                    }
                  </button>
                  <button
                    onClick={() => handlePreview(s)}
                    disabled={previewingId === s.id}
                    className="text-gray-400 hover:text-indigo-600 p-1"
                    title="Preview discovery"
                  >
                    {previewingId === s.id
                      ? <FiLoader className="w-4 h-4 animate-spin" />
                      : <FiEye className="w-4 h-4" />
                    }
                  </button>
                  <button
                    onClick={() => handleRun(s)}
                    disabled={runningId === s.id}
                    className="text-gray-400 hover:text-blue-600 p-1"
                    title="Run now"
                  >
                    {runningId === s.id
                      ? <FiLoader className="w-4 h-4 animate-spin" />
                      : <FiPlay className="w-4 h-4" />
                    }
                  </button>
                  <button
                    onClick={() => { setBackfillScheduler(s); setBackfillDays('90'); }}
                    disabled={s.backfill_status === 'queued' || s.backfill_status === 'running'}
                    className="text-gray-400 hover:text-amber-600 p-1"
                    title="Backfill older references (widen the discovery window for a one-time pass)"
                  >
                    {s.backfill_status === 'queued' || s.backfill_status === 'running'
                      ? <FiLoader className="w-4 h-4 animate-spin text-amber-500" />
                      : <FiRotateCcw className="w-4 h-4" />
                    }
                  </button>
                  <button
                    onClick={() => { setEditingScheduler(s); setShowModal(true); }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    disabled={deletingId === s.id}
                    className="text-gray-400 hover:text-red-500 p-1"
                  >
                    {deletingId === s.id ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Patterns + Ranges */}
              <div className="flex flex-wrap gap-1.5">
                {(s.patterns || []).map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono">
                    {p.pattern || p}
                    <span className="text-gray-400">→ {patternToRegexPreview(p.pattern || p)}</span>
                  </span>
                ))}
                {(s.ref_ranges || []).map((r, i) => (
                  <span key={`range-${i}`} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded font-mono">
                    {r.prefix}{String(r.start).padStart(r.pad, '0')} → {r.prefix}{String(r.end).padStart(r.pad, '0')}
                  </span>
                ))}
              </div>

              {/* Source folders / mailboxes */}
              {((s.source_folder_ids || []).length > 0 || (s.source_mailbox_ids || []).length > 0) && (
                <div className="flex flex-wrap gap-2">
                  {(s.source_folder_ids || []).map((f, i) => (
                    <span key={i} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${f.source === 'doxolve' ? 'text-green-700 bg-green-50' : 'text-blue-700 bg-blue-50'}`} title={f.folder_path || f.folder_name}>
                      {f.source === 'doxolve' ? <FiDatabase className="w-3 h-3" /> : <FiCloud className="w-3 h-3" />}
                      {f.folder_path || f.folder_name || f.folder_id}
                    </span>
                  ))}
                  {(s.source_mailbox_ids || []).map((m, i) => (
                    <span key={i} className="flex items-center gap-1 text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                      <FiMail className="w-3 h-3" /> {[m.mailbox_email, m.folder_name].filter(Boolean).join(' / ') || m.mailbox_id}
                    </span>
                  ))}
                </div>
              )}

              {/* Last/Next run */}
              <div className="flex items-center gap-4 text-xs text-gray-400 pt-1 border-t border-gray-100">
                <span>Last: {fmtDate(s.last_run_at)}</span>
                <span>Next: {fmtDate(s.next_run_at)}</span>
                {s.last_run_summary && (
                  <span className="ml-auto text-gray-500">
                    {s.last_run_summary.sessions_created} created · {s.last_run_summary.sessions_updated} updated · {s.last_run_summary.docs_added} docs
                  </span>
                )}
              </div>
              {s.last_run_error && (
                <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">{s.last_run_error}</div>
              )}
              {s.backfill_status && (
                <div className={`text-xs rounded px-2 py-1 flex items-center gap-1.5 ${
                  s.backfill_status === 'error' ? 'text-red-600 bg-red-50'
                  : (s.backfill_status === 'queued' || s.backfill_status === 'running') ? 'text-amber-700 bg-amber-50'
                  : 'text-gray-500 bg-gray-50'
                }`}>
                  <FiRotateCcw className="w-3 h-3 flex-shrink-0" />
                  {s.backfill_status === 'queued' && `Backfill queued (last ${s.backfill_requested_days} days)`}
                  {s.backfill_status === 'running' && (
                    <>
                      {`Backfill running (last ${s.backfill_requested_days} days)…`}
                      {s.backfill_progress && (
                        <span className="text-amber-600">
                          {' — '}
                          {s.backfill_progress.sessions_created} created · {s.backfill_progress.sessions_updated} updated · {s.backfill_progress.docs_added} docs so far
                          {/* refs_found is each tick's own discovery-pass result, not a running
                              distinct-ref total for the whole 180-day window (confirmed: a
                              scheduler with 189 real distinct synced refs had a most-recent-tick
                              refs_found of only 161 — a partial batch). There's no reliable "Y" to
                              show "X of Y" against, so this only ever shows the largest single
                              batch seen so far as a rough discovery-volume signal — not a
                              completion percentage. */}
                          {!!s.backfill_progress.refs_found && (
                            <> · largest discovery batch so far: {s.backfill_progress.refs_found} refs</>
                          )}
                        </span>
                      )}
                    </>
                  )}
                  {s.backfill_status === 'success' && `Backfill completed ${fmtDate(s.backfill_last_run_at)}`}
                  {s.backfill_status === 'error' && `Backfill failed: ${s.backfill_error || 'unknown error'}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <SchedulerModal
          scheduler={editingScheduler}
          groups={groups}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingScheduler(null); }}
        />
      )}

      {previewResult && (
        <PreviewModal result={previewResult} onClose={() => setPreviewResult(null)} />
      )}

      {backfillScheduler && (
        <BackfillModal
          scheduler={backfillScheduler}
          days={backfillDays}
          onDaysChange={setBackfillDays}
          submitting={backfillSubmitting}
          onSubmit={handleBackfillSubmit}
          onClose={() => setBackfillScheduler(null)}
        />
      )}
    </div>
  );
}

function BackfillModal({ scheduler, days, onDaysChange, submitting, onSubmit, onClose }) {
  const presets = [
    { label: '90 days', value: '90' },
    { label: '6 months', value: '180' },
    { label: '1 year', value: '365' },
  ];
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-800 text-sm">Backfill Older References</h2>
            <p className="text-xs text-gray-400 mt-0.5">{scheduler.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            Ordinary ticks only scan the last 30 days of email for new references — enough to keep
            up with new documents, but too narrow to catch older ones. Use this to run a one-time
            pass over a wider window if you know references from further back haven't been picked
            up yet. This can take several minutes to run in the background.
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">How far back?</label>
            <div className="flex gap-2 mb-2">
              {presets.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => onDaysChange(p.value)}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                    days === p.value
                      ? 'bg-amber-50 border-amber-300 text-amber-700 font-medium'
                      : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="1500"
                value={days}
                onChange={e => onDaysChange(e.target.value)}
                className="w-28 text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <span className="text-xs text-gray-400">days back from today (max 1500)</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-600 text-white font-medium px-4 py-1.5 rounded-lg disabled:opacity-50"
          >
            {submitting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiRotateCcw className="w-4 h-4" />}
            {submitting ? 'Queueing…' : 'Queue Backfill'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ result, onClose }) {
  const { scheduler, refs, excluded } = result;
  const sourceIcon = (type) => type === 'exchange'
    ? <FiMail className="w-3 h-3 text-purple-500 flex-shrink-0" />
    : <FiCloud className="w-3 h-3 text-blue-500 flex-shrink-0" />;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-800 text-sm">Discovery Preview</h2>
            <p className="text-xs text-gray-400 mt-0.5">{scheduler.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* New refs to process */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-700">Will create sessions</span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{refs.length}</span>
            </div>
            {refs.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No new references found in source folders.</p>
            ) : (
              <div className="space-y-1.5">
                {refs.map(r => (
                  <div key={r.ref} className="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="font-mono text-xs font-semibold text-gray-800 w-24 flex-shrink-0">{r.ref}</span>
                    <div className="flex flex-wrap gap-1 flex-1">
                      {r.sources.map((s, i) => (
                        <span key={i} className="flex items-center gap-1 text-xs text-gray-500 bg-white border border-gray-200 rounded px-1.5 py-0.5">
                          {sourceIcon(s.type)} {s.label}
                          {s.itemName && <span className="text-gray-400 ml-0.5">({s.itemName})</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Already-handled refs */}
          {excluded.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-500">Already complete (skipped)</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">{excluded.length}</span>
              </div>
              <div className="space-y-1">
                {excluded.map(r => (
                  <div key={r.ref} className="flex items-center gap-2 text-xs text-gray-400 px-3 py-1.5 bg-gray-50 rounded-lg">
                    <span className="font-mono font-semibold w-24 flex-shrink-0">{r.ref}</span>
                    <span className="truncate">{r.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-4 py-2 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}

function SchedulerModal({ scheduler, groups, onSave, onClose }) {
  const [name, setName] = useState(scheduler?.name || '');
  const [groupId, setGroupId] = useState(scheduler?.group_id || groups[0]?.id || '');
  const [frequencyType, setFrequencyType] = useState(scheduler?.frequency_type || 'daily');
  const [frequencyValue, setFrequencyValue] = useState(scheduler?.frequency_value || 1);
  const [runAtHour, setRunAtHour] = useState(scheduler?.run_at_hour ?? 6);
  const [dateRangeMode, setDateRangeMode] = useState(scheduler?.date_range_mode || 'rolling');
  const [dateRangeDays, setDateRangeDays] = useState(scheduler?.date_range_days || 30);
  const [dateRangeStart, setDateRangeStart] = useState(scheduler?.date_range_start || '');
  const [dateRangeEnd, setDateRangeEnd] = useState(scheduler?.date_range_end || '');
  const [autoComplete, setAutoComplete] = useState(scheduler?.auto_complete || false);
  const [autoArchive, setAutoArchive] = useState(scheduler?.auto_archive || false);
  const [isActive, setIsActive] = useState(scheduler?.is_active !== false);
  const [entryStage, setEntryStage] = useState(scheduler?.entry_stage || 'wip');
  const rfqAvailable = !!groups.find(g => g.id === groupId)?.rfq_enabled;

  useEffect(() => {
    if (!rfqAvailable && entryStage === 'rfq') setEntryStage('wip');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, rfqAvailable]);

  // Patterns
  const [patternInput, setPatternInput] = useState('');
  const [patterns, setPatterns] = useState(
    (scheduler?.patterns || []).map(p => (typeof p === 'string' ? { pattern: p } : p))
  );

  // Ref ranges — e.g. { prefix:'IMP', start:1, end:9999, pad:4 }
  const [rangeInput, setRangeInput] = useState('');
  const [refRanges, setRefRanges] = useState(scheduler?.ref_ranges || []);
  const [rangeError, setRangeError] = useState(null);

  function parseRangeInput(raw) {
    // Accept "IMP0001 to IMP9999" or "IMP0001-IMP9999"
    const clean = raw.trim().toUpperCase().replace(/\s+TO\s+/, '-');
    const m = clean.match(/^([A-Z]+)(\d+)[- ]([A-Z]+)(\d+)$/);
    if (!m) return null;
    if (m[1] !== m[3]) return null;
    const prefix = m[1];
    const start = parseInt(m[2], 10);
    const end = parseInt(m[4], 10);
    const pad = Math.max(m[2].length, m[4].length);
    if (isNaN(start) || isNaN(end) || end < start) return null;
    return { prefix, start, end, pad };
  }

  const addRange = () => {
    setRangeError(null);
    const parsed = parseRangeInput(rangeInput);
    if (!parsed) { setRangeError('Use format: IMP0001 to IMP9999'); return; }
    if (refRanges.find(r => r.prefix === parsed.prefix && r.start === parsed.start && r.end === parsed.end)) return;
    setRefRanges(prev => [...prev, parsed]);
    setRangeInput('');
  };

  const removeRange = (i) => setRefRanges(prev => prev.filter((_, j) => j !== i));

  // Sources — stored as { drive_id, folder_id, folder_name, folder_path, source:'sharepoint' } or { mailbox_id, mailbox_email, folder_id, folder_name, source:'exchange' }
  const [sourceFolders, setSourceFolders] = useState(scheduler?.source_folder_ids || []);
  const [sourceMailboxes, setSourceMailboxes] = useState(scheduler?.source_mailbox_ids || []);
  const [showSourcePicker, setShowSourcePicker] = useState(false);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const addPattern = () => {
    const p = patternInput.trim().toUpperCase();
    if (!p) return;
    if (patterns.find(x => (x.pattern || x) === p)) return;
    setPatterns(prev => [...prev, { pattern: p }]);
    setPatternInput('');
  };

  const removePattern = (p) => setPatterns(prev => prev.filter(x => (x.pattern || x) !== p));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) return setFormError('Name is required');
    if (!groupId) return setFormError('Select a BI group');
    if (patterns.length === 0 && refRanges.length === 0) return setFormError('Add at least one pattern or ref range');
    setSaving(true);
    try {
      await onSave({
        name: name.trim(), group_id: groupId,
        frequency_type: frequencyType, frequency_value: Number(frequencyValue),
        run_at_hour: Number(runAtHour),
        date_range_mode: dateRangeMode,
        date_range_days: Number(dateRangeDays),
        date_range_start: dateRangeMode === 'fixed' ? dateRangeStart : null,
        date_range_end: dateRangeMode === 'fixed' ? dateRangeEnd : null,
        patterns, ref_ranges: refRanges.length > 0 ? refRanges : null,
        source_folder_ids: sourceFolders, source_mailbox_ids: sourceMailboxes,
        // auto_archive only makes sense when auto_complete is on (its checkbox is disabled
        // otherwise) — force it false here too as a backstop, not just via the onChange handler.
        auto_complete: autoComplete, auto_archive: autoComplete && autoArchive, is_active: isActive,
        entry_stage: entryStage,
      });
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <FiClock className="w-4 h-4 text-green-600" />
            {scheduler ? 'Edit Scheduler' : 'New Scheduler'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {formError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</div>
          )}

          {/* Name + Group */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Scheduler Name</label>
              <input
                value={name} onChange={e => setName(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="e.g. Imports Monthly Run"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">BI Group</label>
              <select
                value={groupId} onChange={e => setGroupId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Run every</label>
                <div className="flex gap-2">
                  <input
                    type="number" min="1" max="99"
                    value={frequencyValue} onChange={e => setFrequencyValue(e.target.value)}
                    className="w-16 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <select
                    value={frequencyType} onChange={e => setFrequencyType(e.target.value)}
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              {frequencyType !== 'hourly' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">At hour</label>
                  <select
                    value={runAtHour} onChange={e => setRunAtHour(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                </div>
              )}
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-xs text-gray-500 mb-1">Date range mode</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                  <button type="button"
                    onClick={() => setDateRangeMode('rolling')}
                    className={`flex-1 py-2 px-3 ${dateRangeMode === 'rolling' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >Rolling</button>
                  <button type="button"
                    onClick={() => setDateRangeMode('fixed')}
                    className={`flex-1 py-2 px-3 border-l border-gray-200 ${dateRangeMode === 'fixed' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >Fixed</button>
                </div>
              </div>
              {dateRangeMode === 'rolling' ? (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Lookback (days)</label>
                  <input
                    type="number" min="1" max="3650"
                    value={dateRangeDays} onChange={e => setDateRangeDays(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From date</label>
                    <input
                      type="date"
                      value={dateRangeStart} onChange={e => setDateRangeStart(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To date</label>
                    <input
                      type="date"
                      value={dateRangeEnd} onChange={e => setDateRangeEnd(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Patterns */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Patterns <span className="font-normal text-gray-400">(0 = any digit, A/Z = any letter)</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                value={patternInput}
                onChange={e => setPatternInput(e.target.value.toUpperCase())}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPattern(); } }}
                placeholder="e.g. IMP00000 or PO00000AZ"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button type="button" onClick={addPattern} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-3 py-2 rounded-lg">
                Add
              </button>
            </div>
            {patternInput && (
              <p className="text-xs text-gray-400 mb-2 font-mono">Preview: {patternToRegexPreview(patternInput)}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {patterns.map((p, i) => {
                const pat = p.pattern || p;
                return (
                  <span key={i} className="flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded font-mono">
                    {pat}
                    <span className="text-gray-400">→ {patternToRegexPreview(pat)}</span>
                    <button type="button" onClick={() => removePattern(pat)} className="text-gray-400 hover:text-red-500">
                      <FiX className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Ref Ranges */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Ref Ranges <span className="font-normal text-gray-400">(optional — alternative to patterns, validates refs against a numeric range)</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                value={rangeInput}
                onChange={e => { setRangeInput(e.target.value.toUpperCase()); setRangeError(null); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRange(); } }}
                placeholder="e.g. IMP0001 to IMP9999"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button type="button" onClick={addRange} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-3 py-2 rounded-lg">
                Add
              </button>
            </div>
            {rangeError && <p className="text-xs text-red-500 mb-2">{rangeError}</p>}
            <div className="flex flex-wrap gap-2">
              {refRanges.map((r, i) => (
                <span key={i} className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs px-2 py-1 rounded font-mono">
                  {r.prefix}{String(r.start).padStart(r.pad, '0')} → {r.prefix}{String(r.end).padStart(r.pad, '0')}
                  <button type="button" onClick={() => removeRange(i)} className="text-indigo-400 hover:text-red-500">
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Sources */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-gray-700">
                Source Folders &amp; Mailboxes
              </label>
              <button
                type="button"
                onClick={() => setShowSourcePicker(true)}
                className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded-lg"
              >
                <FiPlus className="w-3.5 h-3.5" /> Add Source
              </button>
            </div>
            {/* Chips for selected sources */}
            {(sourceFolders.length === 0 && sourceMailboxes.length === 0) ? (
              <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-2">
                No sources added. Click "Add Source" to pick SharePoint folders or Exchange mailboxes.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {sourceFolders.map((f, i) => (
                  <span key={i} className={`flex items-center gap-1.5 text-xs border px-2 py-1 rounded-lg ${f.source === 'doxolve' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                    {f.source === 'doxolve' ? <FiDatabase className="w-3 h-3 flex-shrink-0" /> : <FiCloud className="w-3 h-3 flex-shrink-0" />}
                    <span className="max-w-[200px] truncate" title={f.folder_path || f.folder_name}>{f.folder_path || f.folder_name || f.folder_id}</span>
                    <button type="button" onClick={() => setSourceFolders(prev => prev.filter((_, j) => j !== i))} className="text-blue-400 hover:text-red-500 ml-0.5">
                      <FiX className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {sourceMailboxes.map((m, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-1 rounded-lg">
                    <FiMail className="w-3 h-3 flex-shrink-0" />
                    <span className="max-w-[200px] truncate" title={[m.mailbox_email, m.folder_name].filter(Boolean).join(' / ')}>{[m.mailbox_email, m.folder_name].filter(Boolean).join(' / ') || m.mailbox_id}</span>
                    <button type="button" onClick={() => setSourceMailboxes(prev => prev.filter((_, j) => j !== i))} className="text-purple-400 hover:text-red-500 ml-0.5">
                      <FiX className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {showSourcePicker && (
            <SchedulerSourcePicker
              onAdd={(result) => {
                if (result.source === 'sharepoint') {
                  setSourceFolders(prev => [...prev, result]);
                } else {
                  setSourceMailboxes(prev => [...prev, result]);
                }
                setShowSourcePicker(false);
              }}
              onCancel={() => setShowSourcePicker(false)}
            />
          )}

          {/* Entry stage */}
          {rfqAvailable && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <p className="text-xs font-medium text-gray-700 mb-1">Creates sessions in</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEntryStage('rfq')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    entryStage === 'rfq' ? 'bg-amber-50 border-amber-400 text-amber-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  RFQ
                </button>
                <button
                  type="button"
                  onClick={() => setEntryStage('wip')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    entryStage === 'wip' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Work in Progress
                </button>
              </div>
              <p className="text-xs text-gray-500">RFQ sessions are held for manual promotion and are not auto-synced or auto-processed.</p>
            </div>
          )}

          {/* Auto options */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-medium text-gray-700 mb-1">Automation</p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={autoComplete} onChange={e => {
                  const checked = e.target.checked;
                  setAutoComplete(checked);
                  // Auto-archive depends on auto-complete (its checkbox is disabled whenever
                  // this is off) — clear it too so a previously-saved auto_archive:true can't
                  // silently ride along on the next save while looking unchecked/disabled.
                  if (!checked) setAutoArchive(false);
                }}
                className="mt-0.5 w-4 h-4 rounded accent-green-600" />
              <div>
                <span className="text-sm text-gray-800">Auto-complete runs</span>
                <p className="text-xs text-gray-500">Automatically accept and complete a run once all documents are AI-processed</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 cursor-pointer ${!autoComplete ? 'opacity-40 pointer-events-none' : ''}`}>
              <input type="checkbox" checked={autoArchive} onChange={e => setAutoArchive(e.target.checked)}
                disabled={!autoComplete}
                className="mt-0.5 w-4 h-4 rounded accent-purple-600" />
              <div>
                <span className="text-sm text-gray-800">Auto-archive + reports</span>
                <p className="text-xs text-gray-500">After auto-complete: export to archive destination and generate BI, AI and Missing Docs reports</p>
              </div>
            </label>
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)}
              className="w-4 h-4 rounded accent-green-600" />
            <span className="text-sm text-gray-700">Scheduler is active</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-60">
              {saving && <FiLoader className="w-4 h-4 animate-spin" />}
              {scheduler ? 'Save Changes' : 'Create Scheduler'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Scheduler Source Picker ─────────────────────────────────────────────────
// Inline wizard (SharePoint + Exchange only, no doc-type step) that returns
// { source, drive_id, folder_id, folder_name, folder_path } or
// { source, mailbox_id, mailbox_email, folder_id, folder_name }

function SchedulerSourcePicker({ onAdd, onCancel }) {
  const STEPS = { SOURCE: 0, SITE: 1, DRIVE: 2, FOLDER: 3 };
  const [step, setStep] = useState(STEPS.SOURCE);
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [spConnection, setSpConnection] = useState(null);
  const [connectedSites, setConnectedSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSearch, setSiteSearch] = useState('');
  const [connectedDrives, setConnectedDrives] = useState([]);
  const [selectedDrive, setSelectedDrive] = useState(null);
  const [driveSearch, setDriveSearch] = useState('');

  const [exConnection, setExConnection] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedMailbox, setSelectedMailbox] = useState(null);
  const [mailboxSearch, setMailboxSearch] = useState('');

  const [folderStack, setFolderStack] = useState([]);
  const [currentFolders, setCurrentFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderSearch, setFolderSearch] = useState('');

  const filteredSites = useMemo(() => {
    const q = siteSearch.toLowerCase();
    return q ? connectedSites.filter(s => s.site_name?.toLowerCase().includes(q)) : connectedSites;
  }, [connectedSites, siteSearch]);

  const filteredDrives = useMemo(() => {
    const q = driveSearch.toLowerCase();
    return q ? connectedDrives.filter(d => d.drive_name?.toLowerCase().includes(q)) : connectedDrives;
  }, [connectedDrives, driveSearch]);

  const filteredMailboxes = useMemo(() => {
    const q = mailboxSearch.toLowerCase();
    return q ? mailboxes.filter(m => (m.display_name || '').toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)) : mailboxes;
  }, [mailboxes, mailboxSearch]);

  const filteredFolders = useMemo(() => {
    const q = folderSearch.toLowerCase();
    return q ? currentFolders.filter(f => (f.name || f.displayName || '').toLowerCase().includes(q)) : currentFolders;
  }, [currentFolders, folderSearch]);

  const loadDoxolveFolders = async (folderId) => {
    setLoading(true); setError(null);
    try {
      const res = await businessIntelligenceAPI.getDoxolveFolders(folderId);
      setCurrentFolders((res.data.folders || []).map(f => ({ ...f, childCount: 1 })));
      setFolderSearch('');
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const loadSPFolders = async (drive, folderId) => {
    const d = drive || selectedDrive;
    setLoading(true); setError(null);
    try {
      const res = await businessIntelligenceAPI.getSharePointFolders(spConnection.id, d.drive_id, folderId);
      setCurrentFolders(res.data.folders || []);
      setFolderSearch('');
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const loadExFolders = async (mb, folderId) => {
    const m = mb || selectedMailbox;
    setLoading(true); setError(null);
    try {
      const res = await businessIntelligenceAPI.getMailFolders(exConnection.id, m.user_id, folderId);
      setCurrentFolders(res.data.folders || []);
      setFolderSearch('');
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const handleSourceSelect = async (src) => {
    setSource(src); setError(null); setLoading(true);
    try {
      if (src === 'sharepoint') {
        const res = await sharepointAPI.getConnections();
        const active = (res.data.connections || []).filter(c => c.status === 'active');
        if (!active.length) { setError('No active SharePoint connections. Connect one under Integrations first.'); setLoading(false); return; }
        const conn = active[0];
        setSpConnection(conn);
        const raw = conn.connected_sites || [];
        const siteMap = {};
        raw.forEach(s => { if (!siteMap[s.site_id]) siteMap[s.site_id] = { site_id: s.site_id, site_name: s.site_name }; });
        const sites = Object.values(siteMap);
        if (!sites.length) { setError('No connected sites. Use Integrations → SharePoint → Manage first.'); setLoading(false); return; }
        setConnectedSites(sites); setSiteSearch(''); setStep(STEPS.SITE);
      } else if (src === 'doxolve') {
        setFolderStack([]); setSelectedFolder(null);
        await loadDoxolveFolders(null);
        setStep(STEPS.FOLDER);
      } else {
        const res = await exchangeAPI.getConnections();
        const active = (res.data.connections || []).filter(c => c.status === 'active');
        if (!active.length) { setError('No active Exchange connections. Connect one under Integrations first.'); setLoading(false); return; }
        const conn = active[0];
        setExConnection(conn);
        const mbs = conn.connected_mailboxes || [];
        if (!mbs.length) { setError('No mailboxes configured. Use Integrations → Exchange → Manage first.'); setLoading(false); return; }
        setMailboxes(mbs); setMailboxSearch(''); setStep(STEPS.SITE);
      }
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const handleSiteSelect = (site) => {
    setSelectedSite(site); setDriveSearch('');
    const raw = spConnection?.connected_sites || [];
    const drives = raw.filter(s => s.site_id === site.site_id)
      .map(s => ({ drive_id: s.drive_id, drive_name: s.drive_name }))
      .filter((d, i, arr) => arr.findIndex(x => x.drive_id === d.drive_id) === i);
    setConnectedDrives(drives); setSelectedDrive(null); setStep(STEPS.DRIVE);
  };

  const handleDriveSelect = async (drive) => {
    setSelectedDrive(drive); setFolderStack([]); setSelectedFolder(null); setFolderSearch('');
    await loadSPFolders(drive, null); setStep(STEPS.FOLDER);
  };

  const handleMailboxSelect = async (mb) => {
    setSelectedMailbox(mb); setFolderStack([]); setSelectedFolder(null); setFolderSearch('');
    await loadExFolders(mb, null); setStep(STEPS.FOLDER);
  };

  const handleDrillDown = async (folder) => {
    const hasChildren = source === 'sharepoint' ? folder.childCount > 0 : source === 'doxolve' ? true : folder.childFolderCount > 0;
    if (!hasChildren) return;
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name || folder.displayName }]);
    setSelectedFolder(null);
    if (source === 'sharepoint') await loadSPFolders(null, folder.id);
    else if (source === 'doxolve') await loadDoxolveFolders(folder.id);
    else await loadExFolders(null, folder.id);
  };

  const handleFolderBack = async () => {
    const newStack = folderStack.slice(0, -1);
    setFolderStack(newStack); setSelectedFolder(null);
    const parentId = newStack.length > 0 ? newStack[newStack.length - 1].id : null;
    if (source === 'sharepoint') await loadSPFolders(null, parentId);
    else if (source === 'doxolve') await loadDoxolveFolders(parentId);
    else await loadExFolders(null, parentId);
  };

  const handleBack = () => {
    setError(null);
    if (step === STEPS.SITE) { setStep(STEPS.SOURCE); setSource(null); }
    else if (step === STEPS.DRIVE) { setStep(STEPS.SITE); setConnectedDrives([]); setSelectedSite(null); }
    else if (step === STEPS.FOLDER && folderStack.length > 0) { handleFolderBack(); }
    else if (step === STEPS.FOLDER) {
      setCurrentFolders([]);
      if (source === 'sharepoint') setStep(STEPS.DRIVE);
      else if (source === 'doxolve') { setStep(STEPS.SOURCE); setSource(null); }
      else setStep(STEPS.SITE);
    }
  };

  const buildPath = () => {
    if (source === 'sharepoint') {
      return [selectedSite?.site_name, selectedDrive?.drive_name, ...folderStack.map(f => f.name), selectedFolder?.name].filter(Boolean).join(' / ');
    }
    if (source === 'doxolve') {
      return ['Doxolve', ...folderStack.map(f => f.name), selectedFolder?.name].filter(Boolean).join(' / ') || 'Doxolve Root';
    }
    return [selectedMailbox?.email, ...folderStack.map(f => f.name), selectedFolder ? (selectedFolder.displayName || selectedFolder.name) : null].filter(Boolean).join(' / ');
  };

  const handleConfirm = () => {
    const activeFolderId = selectedFolder?.id ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null);
    if (source === 'sharepoint') {
      const activeFolderName = selectedFolder?.name ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].name : selectedDrive.drive_name);
      onAdd({ source: 'sharepoint', drive_id: selectedDrive.drive_id, folder_id: activeFolderId, folder_name: activeFolderName, folder_path: buildPath() });
    } else if (source === 'doxolve') {
      const activeFolderName = selectedFolder?.name ?? (folderStack.length > 0 ? folderStack[folderStack.length - 1].name : 'Root');
      onAdd({ source: 'doxolve', folder_id: activeFolderId, folder_name: activeFolderName, folder_path: buildPath() });
    } else {
      const activeFolderName = selectedFolder ? (selectedFolder.displayName || selectedFolder.name) : (folderStack.length > 0 ? folderStack[folderStack.length - 1].name : 'Inbox');
      onAdd({ source: 'exchange', mailbox_id: selectedMailbox.user_id, mailbox_email: selectedMailbox.email, folder_id: activeFolderId, folder_name: activeFolderName });
    }
  };

  const SearchBox = ({ value, onChange, placeholder }) => (
    <div className="relative mb-2">
      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || 'Search…'}
        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-400 bg-white" />
      {value && <button onClick={() => onChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX className="w-3.5 h-3.5" /></button>}
    </div>
  );

  const renderBreadcrumb = () => {
    const crumbs = source === 'sharepoint'
      ? [selectedSite?.site_name, selectedDrive?.drive_name, ...folderStack.map(f => f.name)].filter(Boolean)
      : source === 'doxolve'
        ? ['Doxolve', ...folderStack.map(f => f.name)].filter(Boolean)
        : [selectedMailbox?.email, ...folderStack.map(f => f.name)].filter(Boolean);
    if (!crumbs.length) return null;
    return (
      <div className="flex items-center flex-wrap gap-1 text-xs bg-gray-50 rounded px-3 py-2">
        {crumbs.map((label, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <FiChevronRight className="w-3 h-3 text-gray-400" />}
            <span className={i === crumbs.length - 1 ? 'font-semibold text-gray-700' : 'text-gray-500'}>{label}</span>
          </span>
        ))}
        {selectedFolder && (
          <span className="flex items-center gap-1">
            <FiChevronRight className="w-3 h-3 text-gray-400" />
            <span className="font-semibold text-blue-600">{selectedFolder.name || selectedFolder.displayName}</span>
          </span>
        )}
      </div>
    );
  };

  const canConfirm = step === STEPS.FOLDER;
  const canGoBack = step > STEPS.SOURCE;

  return (
    <div className="border border-green-200 rounded-xl bg-green-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700">
          {step === STEPS.SOURCE && 'Choose data source'}
          {step === STEPS.SITE && (source === 'sharepoint' ? 'Select connected site' : 'Select mailbox')}
          {step === STEPS.DRIVE && 'Select document library'}
          {step === STEPS.FOLDER && (source === 'doxolve' ? 'Select Doxolve folder' : 'Select folder')}
        </p>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600"><FiX className="w-4 h-4" /></button>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      {/* Step: Source */}
      {step === STEPS.SOURCE && (
        <div className="grid grid-cols-3 gap-3">
          <button type="button" onClick={() => handleSourceSelect('sharepoint')} disabled={loading}
            className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50">
            <FiCloud className="w-8 h-8 text-blue-600" />
            <div><p className="text-sm font-medium text-gray-900">SharePoint</p><p className="text-xs text-gray-500">Connected sites &amp; libraries</p></div>
          </button>
          <button type="button" onClick={() => handleSourceSelect('exchange')} disabled={loading}
            className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors disabled:opacity-50">
            <FiMail className="w-8 h-8 text-purple-600" />
            <div><p className="text-sm font-medium text-gray-900">Exchange</p><p className="text-xs text-gray-500">Connected mailboxes</p></div>
          </button>
          <button type="button" onClick={() => handleSourceSelect('doxolve')} disabled={loading}
            className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors disabled:opacity-50">
            <FiDatabase className="w-8 h-8 text-green-600" />
            <div><p className="text-sm font-medium text-gray-900">Doxolve</p><p className="text-xs text-gray-500">Local document folders</p></div>
          </button>
          {loading && <div className="col-span-3 flex items-center justify-center gap-2 py-2"><FiLoader className="w-4 h-4 animate-spin text-gray-400" /><span className="text-xs text-gray-500">Loading…</span></div>}
        </div>
      )}

      {/* Step: Site / Mailbox */}
      {step === STEPS.SITE && source === 'sharepoint' && (
        <div className="space-y-2">
          <SearchBox value={siteSearch} onChange={setSiteSearch} placeholder="Search sites…" />
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto bg-white">
            {filteredSites.map(site => (
              <li key={site.site_id} onClick={() => handleSiteSelect(site)}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-blue-50 group">
                <FiCloud className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span className="flex-1 text-sm text-gray-800 truncate">{site.site_name}</span>
                <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 flex-shrink-0" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === STEPS.SITE && source === 'exchange' && (
        <div className="space-y-2">
          <SearchBox value={mailboxSearch} onChange={setMailboxSearch} placeholder="Search mailboxes…" />
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto bg-white">
            {filteredMailboxes.map(mb => (
              <li key={mb.user_id} onClick={() => handleMailboxSelect(mb)}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-purple-50 group">
                <FiMail className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{mb.display_name || mb.email}</p>
                  <p className="text-xs text-gray-400 truncate">{mb.email}</p>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-purple-400 flex-shrink-0" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Step: Drive */}
      {step === STEPS.DRIVE && (
        <div className="space-y-2">
          <SearchBox value={driveSearch} onChange={setDriveSearch} placeholder="Search libraries…" />
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto bg-white">
            {filteredDrives.map(drive => (
              <li key={drive.drive_id} onClick={() => handleDriveSelect(drive)}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-yellow-50 group">
                <FiFolder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span className="flex-1 text-sm text-gray-800 truncate">{drive.drive_name}</span>
                <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-yellow-500 flex-shrink-0" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Step: Folder */}
      {step === STEPS.FOLDER && (
        <div className="space-y-2">
          {renderBreadcrumb()}
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {loading ? 'Loading…' : `${currentFolders.length} subfolder${currentFolders.length !== 1 ? 's' : ''}`}
            </p>
            {folderStack.length > 0 && (
              <button type="button" onClick={handleFolderBack} disabled={loading}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50">
                <FiChevronLeft className="w-3.5 h-3.5" /> Up one level
              </button>
            )}
          </div>
          <SearchBox value={folderSearch} onChange={setFolderSearch} placeholder="Search folders…" />

          <div onClick={() => setSelectedFolder(null)}
            className={`flex items-center gap-3 px-3 py-2 border-2 rounded-lg cursor-pointer ${selectedFolder === null ? 'border-blue-400 bg-blue-50' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}>
            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selectedFolder === null ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
              {selectedFolder === null && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
            </div>
            <p className="text-sm text-gray-800">Use this location</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-4"><FiLoader className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-44 overflow-y-auto bg-white">
              {filteredFolders.map(folder => {
                const fName = folder.name || folder.displayName;
                const hasChildren = source === 'sharepoint' ? folder.childCount > 0 : folder.childFolderCount > 0;
                const isSelected = selectedFolder?.id === folder.id;
                return (
                  <li key={folder.id} className={`flex items-center gap-2 px-3 py-2 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                    <div onClick={() => setSelectedFolder(isSelected ? null : folder)}
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center cursor-pointer ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 hover:border-blue-400'}`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <FiFolder className="w-4 h-4 text-yellow-500 flex-shrink-0 cursor-pointer" onClick={() => setSelectedFolder(isSelected ? null : folder)} />
                    <span className="flex-1 text-sm text-gray-800 truncate cursor-pointer" onClick={() => setSelectedFolder(isSelected ? null : folder)}>{fName}</span>
                    {hasChildren && (
                      <button type="button" onClick={() => handleDrillDown(folder)}
                        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0" title={`Open ${fName}`}>
                        <FiChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Footer nav */}
      <div className="flex items-center justify-between pt-1">
        <button type="button" onClick={canGoBack ? handleBack : onCancel} disabled={loading}
          className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 disabled:opacity-50 bg-white">
          <FiChevronLeft className="w-3.5 h-3.5" />{canGoBack ? 'Back' : 'Cancel'}
        </button>
        {canConfirm && (
          <button type="button" onClick={handleConfirm}
            className="flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg px-3 py-1.5">
            <FiCheck className="w-3.5 h-3.5" /> Add Source
          </button>
        )}
      </div>
    </div>
  );
}
